// Ranking for the ⌘K quick switcher. Pure and framework-free (tested in switcher.test.ts).

export type SwitcherKind = 'channel' | 'dm' | 'group' | 'person' | 'browse'

export interface SwitcherCandidate {
  /** Conversation id, or user id for people without a DM yet. */
  id: string
  kind: SwitcherKind
  /** Primary label as shown (channel name without '#', person's display name, group participants). */
  label: string
  /** Other names that should match as well as the label: real name, handle, participants. */
  aliases?: string[]
  /** Weaker context that can still match: job title, channel topic or purpose. */
  keywords?: string[]
  /** Last time this was opened from the switcher or sidebar (ms). */
  recentAt?: number
  unread?: number
  mentions?: number
  /** Archived channels, deactivated users: shown last. */
  inactive?: boolean
  /** The conversation you're in: left out of the empty state so ⌘K ↵ jumps back to the previous one. */
  current?: boolean
}

export interface SwitcherMatch<T extends SwitcherCandidate = SwitcherCandidate> {
  item: T
  score: number
  /** Character indices of `item.label` to highlight. */
  highlight: number[]
}

/** Lowercases and strips accents so "jose" finds "José". Keeps string length stable. */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function foldWithMap(s: string): { text: string; map: number[] } {
  let text = ''
  const map: number[] = []
  for (let i = 0; i < s.length; i++) {
    const folded = fold(s[i])
    for (const ch of folded) {
      text += ch
      map.push(i)
    }
  }
  return { text, map }
}

const WORD_BREAK = /[\s\-_.,/()@#]/

function wordStarts(text: string): number[] {
  const starts: number[] = []
  for (let i = 0; i < text.length; i++) if (i === 0 || (WORD_BREAK.test(text[i - 1]) && !WORD_BREAK.test(text[i]))) starts.push(i)
  return starts
}

const range = (from: number, len: number) => Array.from({ length: len }, (_, i) => from + i)

interface FieldMatch {
  score: number
  /** Indices into the folded text. */
  at: number[]
}

/**
 * Scores how well `q` (folded, no spaces at the ends) matches `text`.
 * exact > prefix > word prefix > all words > initials > substring > fuzzy subsequence.
 */
export function matchField(q: string, rawText: string, fuzzy = true): FieldMatch | null {
  const text = fold(rawText)
  if (!q || !text) return null
  if (text === q) return { score: 1000, at: range(0, q.length) }
  if (text.startsWith(q)) return { score: 900 - Math.min(text.length - q.length, 50), at: range(0, q.length) }

  const starts = wordStarts(text)
  for (const s of starts) if (text.startsWith(q, s)) return { score: 800 - Math.min(s, 50), at: range(s, q.length) }

  // Every query word prefixes some word in the text: "maya ch" → "Maya Chen", "chen maya" too.
  const qWords = q.split(/\s+/).filter(Boolean)
  if (qWords.length > 1) {
    const at: number[] = []
    const used = new Set<number>()
    const ok = qWords.every((w) => {
      const s = starts.find((s) => !used.has(s) && text.startsWith(w, s))
      if (s === undefined) return false
      used.add(s)
      at.push(...range(s, w.length))
      return true
    })
    if (ok) return { score: 760, at }
  }

  // Initials: "mc" → "Maya Chen", "ep" → "eng-platform".
  const compact = q.replace(/\s+/g, '')
  if (compact.length >= 2 && compact.length <= starts.length) {
    for (let from = 0; from + compact.length <= starts.length; from++) {
      const slice = starts.slice(from, from + compact.length)
      if (slice.every((s, i) => text[s] === compact[i])) return { score: 700 - from * 10, at: slice }
    }
  }

  // A single character only matches at the start of a word; anything looser is noise.
  if (compact.length < 2) return null

  const sub = text.indexOf(q)
  if (sub >= 0) return { score: 500 - Math.min(sub, 50), at: range(sub, q.length) }

  // Fuzzy subsequence, rewarding tight runs and word starts.
  if (!fuzzy) return null
  const at: number[] = []
  let ti = 0
  for (const ch of compact) {
    const found = text.indexOf(ch, ti)
    if (found < 0) return null
    at.push(found)
    ti = found + 1
  }
  const spread = at[at.length - 1] - at[0] + 1 - at.length
  if (spread > Math.max(6, compact.length * 3)) return null
  const startBonus = at.filter((i) => starts.includes(i)).length * 15
  return { score: Math.max(1, 300 - spread * 12 + startBonus), at }
}

const KIND_WEIGHT: Record<SwitcherKind, number> = { dm: 45, channel: 40, person: 35, group: 15, browse: 0 }

/** Parses a leading "#" (channels only) or "@" (people only). */
export function parseQuery(raw: string): { text: string; only: 'channels' | 'people' | null } {
  const trimmed = raw.trimStart()
  const only = trimmed.startsWith('#') ? 'channels' : trimmed.startsWith('@') ? 'people' : null
  return { text: fold((only ? trimmed.slice(1) : trimmed).trim()).replace(/\s+/g, ' '), only }
}

const isPeople = (k: SwitcherKind) => k === 'dm' || k === 'person' || k === 'group'

/**
 * Ranks candidates for `query`. With an empty query, returns recents first, then
 * unread conversations, then everything else the user is a member of.
 */
export function rankSwitcher<T extends SwitcherCandidate>(candidates: T[], query: string, now = Date.now(), limit = 50): SwitcherMatch<T>[] {
  const { text: q, only } = parseQuery(query)
  const pool = candidates.filter((c) => (only === 'channels' ? !isPeople(c.kind) : only === 'people' ? isPeople(c.kind) : true))

  if (!q) {
    const browse = only === 'channels'
    return pool
      .filter((c) => (browse ? true : c.kind !== 'browse' && c.kind !== 'person') && !c.inactive && !c.current)
      .map((item) => {
        const recency = item.recentAt ? 1000 + Math.max(0, 500 - (now - item.recentAt) / 60_000) : 0
        const unread = (item.mentions ? 300 : 0) + (item.unread ? 200 : 0)
        return { item, score: recency + unread + KIND_WEIGHT[item.kind], highlight: [] }
      })
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
      .slice(0, limit)
  }

  const out: SwitcherMatch<T>[] = []
  for (const item of pool) {
    const label = matchField(q, item.label)
    let best = label ? label.score : 0
    for (const alias of item.aliases ?? []) {
      const m = matchField(q, alias)
      // Alias hits rank just under an equally good label hit.
      if (m && m.score - 5 > best) best = m.score - 5
    }
    for (const keyword of item.keywords ?? []) {
      const m = matchField(q, keyword, false)
      if (m && m.score / 2 > best) best = m.score / 2
    }
    if (!best) continue
    let score = best + KIND_WEIGHT[item.kind]
    if (item.recentAt) score += Math.max(0, 120 - (now - item.recentAt) / (60 * 60_000)) // decays over ~5 days
    if (item.mentions) score += 25
    else if (item.unread) score += 10
    if (item.inactive) score -= 600
    let highlight: number[] = []
    if (label) {
      const { map } = foldWithMap(item.label)
      highlight = [...new Set(label.at.map((i) => map[i]).filter((i) => i !== undefined))]
    }
    out.push({ item, score, highlight })
  }
  return out.sort((a, b) => b.score - a.score || a.item.label.length - b.item.label.length || a.item.label.localeCompare(b.item.label)).slice(0, limit)
}

/** Splits `text` into runs for rendering highlighted matches. */
export function highlightRuns(text: string, indices: number[]): { text: string; hit: boolean }[] {
  if (!indices.length) return [{ text, hit: false }]
  const hits = new Set(indices)
  const runs: { text: string; hit: boolean }[] = []
  for (let i = 0; i < text.length; i++) {
    const hit = hits.has(i)
    const last = runs[runs.length - 1]
    if (last && last.hit === hit) last.text += text[i]
    else runs.push({ text: text[i], hit })
  }
  return runs
}
